import errorHandlers from '@/services/error-handlers';
import events from '@/services/events';
import http from '@/services/http';
import parseLoyalty from '@/helpers/parse-loyalty';

export default {
  namespaced: true,

  state: {
    allOffers: [],
    areLoading: false,
    loyalty: null,
    loyaltyTransactions: {
      hasMore: false,
      activities: []
    },
    giftingPoints: {
      currencyPluralName: null,
      pointGiftClaimCode: null,
      claimGiftedPointsFlow: null
    },
    promoCode: {
      claimCode: null,
      claimType: null
    },
    retroReceiptToken: null,
    moreOffersLoading: false
  },

  getters: {
    loyalty(state, getters, rootState, rootGetters) {
      const currentUser = rootGetters['session/currentUser'];
      const merchant = rootGetters['merchant/merchant'];
      const order = rootGetters['orders/order'];
      const { loyalty } = state;

      return parseLoyalty({
        isEnrolled: currentUser.isEnrolledInLoyalty,
        loyalty,
        merchant,
        order
      });
    },

    offers(state, getters, rootState, rootGetters) {
      const order = rootGetters['orders/order'];

      const allOffers = state.allOffers.map((offer) => {
        const couponImage = offer.coupons.find(coupon => coupon.channelName === 'loyaltyRewardTier');

        const couponDetailsImage = offer.coupons.find(coupon => coupon.channelName === 'app');

        const couponDetailsImageUrl = couponDetailsImage?.defaultImagePath || null;

        const couponImageUrl = couponImage?.defaultImagePath || couponDetailsImageUrl;

        const appliedOffer = order.offers.find(o => (
          o.offerAllocationPublicId === offer.offerAllocationPublicId
          && o.applicationStatus === 'Applied'
        ));

        return {
          ...offer,
          appliedOffer,
          couponImageUrl,
          couponDetailsImageUrl
        };
      });

      const allRedeemableOffers = allOffers.filter(offer => (
        offer.status === 'Redeemable'
        && offer.redemptionChannels.includes('WLApp')
      ));

      const offersByType = allOffers.reduce((obj, offer) => {
        const type = offer.type.toLowerCase();

        if (obj[type]) obj[type].push(offer);
        else obj[type] = [offer];

        return obj;
      }, {});


      return {
        ...state,
        allOffers,
        allRedeemableOffers,
        offersByType
      };
    },

    rewardCount(state, getters) {
      const unlockedThresholds = getters.loyalty?.unlockedThresholds || [];

      return getters.offers.allOffers.length + unlockedThresholds.length;
    }
  },

  actions: {
    async convertPendingPoints({ rootGetters }, publicId) {
      const disableInStoreRedemption = rootGetters['merchant/merchant'].options.loyaltyOptions?.inStoreLoyaltyOptions?.disableInStoreRedemption;
      const startOrderFromOffer = rootGetters['merchant/merchant'].options.loyaltyOptions?.inStoreLoyaltyOptions?.startOrderFromOffer;

      try {
        const pendingOffer = await http.post('/cardfree-api/v1/account/me/pending-redemptions', { conversionThresholdPublicId: publicId });

        if (!startOrderFromOffer && !disableInStoreRedemption) {
          return pendingOffer.data;
        }
      }
      catch (error) {
        errorHandlers.generic(error);
      }
    },

    async convertPointsByThreshold({ dispatch, state, rootGetters }, threshold) {
      const disableInStoreRedemption = rootGetters['merchant/merchant'].options.loyaltyOptions?.inStoreLoyaltyOptions?.disableInStoreRedemption;
      const startOrderFromOffer = rootGetters['merchant/merchant'].options.loyaltyOptions?.inStoreLoyaltyOptions?.startOrderFromOffer;

      const wait = ms => new Promise((r) => {
        setTimeout(r, ms);
      });
      const findRedeemedOffer = () => state.allOffers.find(o => o.cardfreeOfferPublicId === threshold.rewardOffer.cardfreeOfferPublicId);

      await http.post('/cardfree-api/v1/account/me/convert-points-by-threshold', { conversionThresholdPublicId: threshold.publicId });

      let redeemedOffer = null;

      await wait(1000);
      await dispatch('getOffers', { silent: true });
      redeemedOffer = findRedeemedOffer();

      if (!redeemedOffer) {
        await wait(4000);
        await dispatch('getOffers', { silent: true });
        redeemedOffer = findRedeemedOffer();
      }

      if (!redeemedOffer) {
        await wait(5000);
        await dispatch('getOffers', { silent: true });
        redeemedOffer = findRedeemedOffer();
      }

      if (redeemedOffer) {
        if (!startOrderFromOffer && !disableInStoreRedemption) {
          dispatch('modals/openModal', {
            name: 'offerInStoreRedemption',
            data: { offer: redeemedOffer }
          }, { root: true });
        }
      }
      else {
        events.emit('notification', {
          clickToDismiss: true,
          message: 'Your offer is taking a while to process. Please check your rewards later.',
          type: 'error'
        });
      }
    },

    async getLoyaltySummary({ commit, rootGetters }, options = {}) {
      try {
        const merchantLoyaltyOptions = rootGetters['merchant/loyaltyOptions'];

        if (options.loyaltyMember && !merchantLoyaltyOptions.isStoreSpecificLoyalty) {
          commit('SET_LOYALTY_SUMMARY', options.loyaltyMember);

          return;
        }

        const currentUser = rootGetters['session/currentUser'];
        const timeZone = rootGetters['locations/timeZone'];

        if (!merchantLoyaltyOptions.loyaltyEnabled || !currentUser.accountId || !currentUser.isEnrolledInLoyalty) {
          return;
        }

        const body = { params: { ianaTimeZoneId: timeZone } };

        if (merchantLoyaltyOptions.isStoreSpecificLoyalty) {
          Object.assign(body, { params: { ...body.params, storeId: options.storeId } });
        }

        const response = await http.get('/cardfree-api/v1/account/me/loyaltyprogram', body);

        commit('SET_LOYALTY_SUMMARY', response.data);
      }

      catch (error) {
        commit('SET_LOYALTY_SUMMARY', null);

        errorHandlers.silent(error);
      }
    },

    async getOffers({ commit, dispatch, rootGetters }, options = {}) {
      try {
        const currentUser = rootGetters['session/currentUser'];
        const merchant = rootGetters['merchant/merchant'];
        const timeZone = rootGetters['locations/timeZone'];

        if (!merchant.options.offersOptions.offersEnabled || !currentUser.accountId) {
          return;
        }

        if (!options.silent) {
          commit('SET_LOAD_STATUS', true);
        }

        const response = await http.get('/cardfree-api/v1/offer/me?rc=WebUrl&rc=WLApp&rc=PosIntegratedAppRedemptionCode', {
          params: {
            ianaTimeZoneId: timeZone,
            includeRedemptionCodePending: true
          }
        });

        commit('SET_OFFERS', response.data);

        await dispatch('getLoyaltySummary', {
          loyaltyMember: options.loyaltyMember
        });
      }

      catch (error) {
        if (options.silentError) {
          errorHandlers.silent(error);
        }

        else {
          errorHandlers.generic(error);
        }
      }

      finally {
        commit('SET_LOAD_STATUS', false);
      }
    },

    async getLoyaltyTransactions({ commit, rootGetters }, requestDataOffset) {
      const timeZone = rootGetters['locations/timeZone'];

      const requestOptions = {
        params: {
          ianaTimeZoneId: timeZone,
          limit: 25,
          offset: requestDataOffset
        }
      };

      try {
        if (requestDataOffset > 0) {
          commit('SET_HAS_MORE_OFFERS_LOAD_STATUS', true);
        }
        else {
          commit('RESET_LOYALTY_TRANSACTIONS');

          commit('SET_LOAD_STATUS', true);
        }

        const response = await http.get('/cardfree-api/v1/account/me/member-activities', requestOptions);

        commit('SET_LOYALTY_TRANSACTIONS', response.data);
      }
      catch (error) {
        errorHandlers.generic(error);
      }
      finally {
        if (requestDataOffset > 0) {
          commit('SET_HAS_MORE_OFFERS_LOAD_STATUS', false);
        }
        else {
          commit('SET_LOAD_STATUS', false);
        }
      }
    },

    async sendGiftedLoyaltyPoints(context, payload) {
      const body = { ...payload };

      const eventPayload = {
        type: 'success',
        message: `${payload.numberOfPoints} points gifted to ${payload.recipientEmailAddress}`
      };

      try {
        await http.post('/cardfree-api/v1/account/me/point-gifts', body);

        events.emit('notification', eventPayload);
      }
      catch (error) {
        errorHandlers.generic(error);
      }
    },

    async claimPromoCode({ state, rootGetters, dispatch, commit }) {
      const currentUser = rootGetters['session/currentUser'];

      const merchant = rootGetters['merchant/merchant'];

      const isAppDownloadConfigured = merchant.features.aosUrl || merchant.features.iosUrl;

      const isLandingPage = merchant.features['enable-gift-claim-app-download-landing-page'];

      try {
        const response = await http.post(`/cardfree-api/v1/account/${currentUser.accountId}/gift-claims`, state.promoCode);

        if (isLandingPage && isAppDownloadConfigured) {
          commit('session/SET_SIGN_IN_REDIRECT_ROUTE', { name: 'appDownloadPage' }, { root: true });
        }
        else {
          const inAppMessage = { type: 'ClaimConfirmation', publicId: response.data.merchantInAppMessagePublicId };

          dispatch('merchant/openInAppMessage', inAppMessage, { root: true });
        }
      }
      catch (error) {
        errorHandlers.generic(error);
      }
      finally {
        commit('SET_PROMO_CODE_DATA', { claimType: null, claimCode: null });
      }
    },

    async claimGiftedLoyaltyPoints({ state, dispatch, commit }) {
      const { pointGiftClaimCode, claimGiftedPointsFlow } = state.giftingPoints;

      const emitSuccessNotification = ({ data }) => {
        dispatch('modals/closeModal', 'claimGiftedPointsModal', { root: true });

        const eventPayload = {
          type: 'success',
          message: `You received ${data.numberOfPoints} ${data.currencyPluralName} from ${data.senderFirstName} ${data.senderLastName}`
        };

        events.emit('notification', eventPayload);
      };

      const openClaimGiftedPointsModal = ({ data }) => {
        const modalPayload = {
          name: 'claimGiftedPointsModal',
          data: {
            viewType: 'SUCCESS',
            giftedPoints: data.numberOfPoints
          }
        };

        dispatch('modals/openModal', modalPayload, { root: true });
      };

      const claimFlowMap = {
        SIGNED_IN: emitSuccessNotification,
        CREATED_ACCOUNT: openClaimGiftedPointsModal
      };

      try {
        const response = await http.post(`/cardfree-api/v1/account/me/point-gifts/${pointGiftClaimCode}/claim`);

        const responseHandler = claimFlowMap[claimGiftedPointsFlow];

        responseHandler(response);
      }
      catch (error) {
        errorHandlers.generic(error);
      }
      finally {
        commit('SET_GIFTING_POINTS_DATA', {
          pointGiftClaimCode: null,
          claimGiftedPointsFlow: null,
          currencyPluralName: null
        });
      }
    },

    async claimPreviousOrder({ commit }, { type, value }) {
      try {
        /* Note: type = posReceiptToken || cfReceiptToken */
        const body = { [type]: value };

        const response = await http.post('/cardfree-api/v1/account/me/retro-claims', body);

        if (response.data.loyaltyGranted) {
          events.emit('notification', {
            type: 'success',
            duration: 5000,
            message: 'Order and loyalty activity claimed to your account!'
          });
        }
        else {
          events.emit('notification', {
            type: 'success',
            duration: 5000,
            message: 'Order successfully claimed to your account!'
          });
        }

        return true;
      }
      catch (error) {
        const isNotFound = error.response?.status === 404;

        if (isNotFound) {
          const errorOpts = { fallbackMessage: 'This order is ineligible to be claimed' };

          errorHandlers.generic(error, errorOpts);
        }
        else {
          errorHandlers.generic(error);
        }

        return false;
      }
      finally {
        commit('SET_RETRO_RECEIPT_TOKEN', null);
      }
    }
  },

  mutations: {
    SET_LOAD_STATUS(state, loading) {
      state.areLoading = loading;
    },

    SET_HAS_MORE_OFFERS_LOAD_STATUS(state, loadingStatus) {
      state.moreOffersLoading = loadingStatus;
    },

    SET_LOYALTY_SUMMARY(state, loyalty) {
      state.loyalty = loyalty;
    },

    SET_OFFERS(state, offers) {
      const allOffers = offers.map((offer) => {
        const o = {
          ...offer,
          ...offer.offer
        };
        delete o.offer;
        return o;
      });

      state.allOffers = allOffers;
    },

    SET_LOYALTY_TRANSACTIONS(state, transactionData) {
      const { hasMore, activities } = transactionData;

      state.loyaltyTransactions = {
        hasMore,
        activities: [...state.loyaltyTransactions.activities, ...activities]
      };
    },

    RESET_LOYALTY_TRANSACTIONS(state) {
      state.loyaltyTransactions = {
        hasMore: false,
        activities: []
      };
    },

    SET_GIFTING_POINTS_DATA(state, data) {
      state.giftingPoints = { ...state.giftingPoints, ...data };
    },

    SET_PROMO_CODE_DATA(state, data) {
      state.promoCode = { ...state.promoCode, ...data };
    },

    SET_RETRO_RECEIPT_TOKEN(state, data) {
      state.retroReceiptToken = data;
    }
  }
};
